export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";
import { returnToForApiPlan, type ApiPlanId } from "@shared/apiPlanCarry";
import { returnToForWorkflow, type EventSuiteBuyPlan, type WorkflowBuyId } from "@shared/workflowCarry";

/**
 * Switches for the site's ad surfaces.
 *
 * ADS_ENABLED: Google AdSense (InlineAd on tool pages, InArticleAd on blog
 * posts in AdBanner.tsx). AdSense approved the site on 24 Oct 2026, so this
 * is on. Pro subscribers never see ads, and visitors in the EEA/UK/CH only
 * get the ad script after they opt in (see lib/consent.ts).
 *
 * INFOLINKS_ENABLED: the Infolinks in-text network. It was paused during the
 * AdSense review and stays off until the owner decides otherwise, so the
 * pages stay uncluttered while AdSense starts serving.
 */
export const ADS_ENABLED = true;
export const INFOLINKS_ENABLED = false;

/** Kicks off the "Sign in with Google" flow.
 *
 * Defaults to sending the person back to the exact page they were on
 * once sign-in completes (e.g. the tool page they clicked "Quick Buy"
 * from), rather than always landing back on the homepage -- without
 * this, an unauthenticated visitor had to navigate back and click
 * "Quick Buy" a second time after signing in just to reach checkout.
 * Pass an explicit returnTo only when the current page genuinely isn't
 * where they should land back on. */
export const getLoginUrl = (returnTo?: string) => {
  const path = returnTo ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  return `/api/auth/google/login?returnTo=${encodeURIComponent(path)}`;
};

/** Same as getLoginUrl(), but also marks which tool plan the person
 * was trying to buy -- useAutoResumeCheckout (mounted globally in
 * Layout.tsx) watches for this once they're back and authenticated,
 * and finishes the checkout automatically instead of just returning
 * them to the page with the same "Quick Buy" click still ahead of
 * them. Returning to the tool page alone isn't enough on its own;
 * this is what actually resumes the purchase. */
export const getLoginUrlForPlan = (plan: "day_pass" | "monthly" | "premium_day_pass" | "premium_monthly") => {
  const url = new URL(window.location.href);
  url.searchParams.set("autoBuy", plan);
  return getLoginUrl(url.pathname + url.search);
};

/** Same idea for an API plan (Premium, Ultra, Mega): sign in, come back to this page, and go straight to Stripe Checkout for that plan.
 * Only the plan name travels in the returnTo; see shared/apiPlanCarry.ts. */
export const getLoginUrlForApiPlan = (plan: ApiPlanId) =>
  getLoginUrl(returnToForApiPlan(plan, window.location.pathname, window.location.search));

/** Same idea for a Workflow Product (Book Writing Toolbox, Event Suite, Seller Studio, Legal Production Desk): sign in, come back to this
 * page, and go straight to Stripe Checkout for that product. Only the product id travels in the returnTo; see shared/workflowCarry.ts. */
export const getLoginUrlForWorkflow = (product: WorkflowBuyId, eventPlan: EventSuiteBuyPlan = "standard") =>
  getLoginUrl(returnToForWorkflow(product, window.location.pathname, window.location.search, eventPlan));
